import {useEffect, useState} from "react";
import State from "./State";
import { useToasts } from "../Components/Toasts";
import API from "../Services/API";

const promises = {};

const Model = (props) => {
    const {
        fetchData, returnDataKey, stateKey, defaultData, saveAdditionalData,
        block, alwaysRefresh, defaultLoadingError, registerAdditionalMethods,
    } = props;

    const {showError, showSuccess} = useToasts();
    const _stateKey = Array.isArray(stateKey) ? stateKey.join('-') : stateKey;

    const [model, setModel] = useState(defaultData ?? State.get(stateKey, null));
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasLoaded, setHasLoaded] = useState({});

    const doFetchData = async (force = false) => {
        if (block === true) {
            return;
        }

        if (State.get(_stateKey) !== null && force !== true) {
            setModel(State.get(_stateKey));

            if ((hasLoaded[_stateKey] === true || alwaysRefresh !== true) && force === false) {
                setIsLoading(false);
                return;
            }
        }

        setHasLoaded(Object.assign({}, hasLoaded, {[_stateKey]: true}));
        setIsLoading(true);
        const response = await handleFetchData(_stateKey, fetchData, showError, defaultLoadingError);

        delete promises[_stateKey];

        State.put(_stateKey, response);

        if (saveAdditionalData) {
            saveAdditionalData(response);
        }

        setModel(response);
        setIsLoading(false);
    }

    useEffect(() => {
        doFetchData(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_stateKey])

    let additionalMethods = {};
    if (registerAdditionalMethods) {
        additionalMethods = registerAdditionalMethods({
            model: () => model,
            updateModel: (newModel) => {
                State.put(_stateKey, newModel);
                setModel(newModel);
            },
            refresh: () => doFetchData(true),
            setIsSaving,
            showError,
            showSuccess,
            wrapUpdateCall: (fn, successMsg, errorMsg, refreshAfterCall = true) => {
                setIsSaving(true);
                return fn().then((response) => {
                    setIsSaving(false);
                    refreshAfterCall && doFetchData(true);
                    successMsg && showSuccess(successMsg);
                    return response;
                }).catch((err) => {
                    setIsSaving(false);
                    if (errorMsg === true) {
                        showError(API.parseErrorResponse(err, defaultLoadingError ?? 'Sorry, something went wrong.'));
                    } else if (errorMsg) {
                        showError(errorMsg);
                    }
                    throw err;
                });
            }
        });
    }

    const returnData = {
        refresh: () => doFetchData(true),
        isLoading: (isLoading || promises[_stateKey] || model === null) ?? false,
        isEmpty: (Array.isArray(model) && model.length === 0),
        ...additionalMethods,
        isSaving,
        doWait: async () => {
            return promises[_stateKey] ?? null;
        }
    };
    returnData[returnDataKey] = model;

    return returnData;
}

const handleFetchData = async (_stateKey, fetchData, showError, defaultLoadingError) => {
    if (promises[_stateKey]) {
        return promises[_stateKey];
    }

    return promises[_stateKey] = fetchData().catch(err => {
        if (err && err?.[0]) {
            showError(err[0]?.title ?? defaultLoadingError, err[0]?.detail);
        } else {
            showError(API.parseErrorResponse(err, defaultLoadingError ?? 'Sorry, something went wrong.'));
        }
        throw err;
    });
}

export default Model;
